// Item-level photo upload UI (slice 12).
//
// Scope locked by the slice 12 recommendation: a native file input, a
// local preview via URL.createObjectURL, a progress bar, a Confirm
// button, and a "processing" state that polls GET photo-url every
// 3s until at least one derivative row exists. Deliberately NOT in
// this slice: drag-and-drop, cropping, bulk upload, image deletion,
// orientation preview. Those are all follow-ups once a real owner
// asks.
//
// The GET photo-url query is enabled only when the item has a
// photoObjectKey. refetchInterval returns false once derivatives are
// present, so polling stops as soon as the worker finishes. This keeps
// the admin cheap for the common case (nothing to poll).

import { useEffect, useRef, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from '../../api/client.js';
import type { MenuItemRow, PhotoDerivative, PhotoUrlResponse } from '../../api/types.js';
import {
  PhotoValidationError,
  uploadMenuItemPhoto,
  type PhotoUploadResult,
} from '../../api/photo-upload.js';

interface ItemPhotoEditorProps {
  readonly orgId: string;
  readonly menuId: string;
  readonly detailKey: readonly unknown[];
  readonly item: MenuItemRow;
}

export function ItemPhotoEditor(props: ItemPhotoEditorProps): JSX.Element {
  const { orgId, menuId, detailKey, item } = props;
  const basePath = `/organizations/${orgId}/menus/${menuId}/items/${item.id}`;
  const qc = useQueryClient();

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Revoke the object URL when the previewed file changes or the row unmounts.
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const photoUrl = useQuery({
    queryKey: ['photo-url', orgId, menuId, item.id, item.photoObjectKey],
    queryFn: () => fetchPhotoUrls(basePath),
    enabled: item.photoObjectKey !== null,
    refetchInterval: (q) => {
      const data = q.state.data as PhotoUrlResponse | undefined;
      return data && data.derivatives.length > 0 ? false : 3000;
    },
  });

  const upload = useMutation<PhotoUploadResult, Error, File>({
    mutationFn: (f) =>
      uploadMenuItemPhoto({
        basePath,
        file: f,
        expectedVersion: item.version,
        onProgress: setProgress,
      }),
    onSuccess: () => {
      setFile(null);
      setProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
      qc.invalidateQueries({ queryKey: detailKey });
    },
  });

  function onPickFile(f: File | undefined): void {
    setValidationError(null);
    upload.reset();
    if (!f) {
      setFile(null);
      return;
    }
    setFile(f);
  }

  function onConfirm(): void {
    if (!file) return;
    setProgress(0);
    try {
      upload.mutate(file);
    } catch (e) {
      if (e instanceof PhotoValidationError) {
        setValidationError(e.message);
      } else {
        throw e;
      }
    }
  }

  const hasPhoto = item.photoObjectKey !== null;
  const derivatives = photoUrl.data?.derivatives ?? [];
  const processing = hasPhoto && derivatives.length === 0;
  const previewSrc = pickSmallestWebp(derivatives) ?? photoUrl.data?.originalUrl ?? null;

  return (
    <section className="photo-editor" aria-label={`photo for item ${item.id}`}>
      {hasPhoto ? (
        <div className="photo-preview">
          {previewSrc ? (
            <img src={previewSrc} alt="" width={160} height={120} loading="lazy" />
          ) : null}
          {processing ? (
            <p className="muted" role="status">
              Processing… the worker is generating image sizes.
            </p>
          ) : (
            <p className="muted">
              {derivatives.length} derivative{derivatives.length === 1 ? '' : 's'} ready.
            </p>
          )}
        </div>
      ) : null}

      <label htmlFor={`photo-${item.id}`}>{hasPhoto ? 'Replace photo' : 'Upload photo'}</label>
      <input
        id={`photo-${item.id}`}
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        onChange={(e) => onPickFile(e.target.files?.[0])}
        disabled={upload.isPending}
      />

      {file ? (
        <div className="photo-staged">
          {previewUrl ? <img src={previewUrl} alt="" width={120} height={90} /> : null}
          <div>
            <p className="muted">
              {file.name} · {formatBytes(file.size)}
            </p>
            <button type="button" onClick={onConfirm} disabled={upload.isPending}>
              {upload.isPending ? 'Uploading…' : 'Confirm'}
            </button>
          </div>
        </div>
      ) : null}

      {upload.isPending ? <progress aria-label="upload progress" value={progress} max={1} /> : null}

      {validationError ? (
        <p role="alert" className="error">
          {validationError}
        </p>
      ) : null}
      {upload.error ? (
        <p role="alert" className="error">
          {upload.error instanceof PhotoValidationError
            ? upload.error.message
            : upload.error instanceof ApiError
              ? upload.error.message
              : 'upload failed'}
        </p>
      ) : null}
    </section>
  );
}

function fetchPhotoUrls(basePath: string): Promise<PhotoUrlResponse> {
  return apiFetch<PhotoUrlResponse>(`${basePath}/photo-url`);
}

function pickSmallestWebp(derivatives: readonly PhotoDerivative[]): string | null {
  const sorted = [...derivatives]
    .filter((d) => d.format === 'webp')
    .sort((a, b) => a.width - b.width);
  return sorted[0]?.url ?? null;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
